import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Search } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface CoverPhotoSearchProps {
  onSelectPhoto: (photoUrl: string) => void;
}

const quickSuggestions = ["Roof", "Siding", "Gutters", "Windows", "Deck"];

export function CoverPhotoSearch({ onSelectPhoto }: CoverPhotoSearchProps) {
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [photos, setPhotos] = useState<Array<{ name: string; url: string }>>([]);

  const generatePhotos = async (query: string) => {
    if (!query.trim()) {
      toast.error("Please enter a search term");
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-cover-photos', {
        body: { searchQuery: query }
      });
      
      if (error) throw error;
      
      if (data?.images) {
        setPhotos(data.images);
        toast.success(`Generated ${data.images.length} photos for "${query}"`);
      }
    } catch (error) {
      console.error('Error generating photos:', error);
      toast.error("Failed to generate photos");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    generatePhotos(searchQuery);
  };

  const handleQuickSearch = (suggestion: string) => {
    setSearchQuery(suggestion);
    generatePhotos(suggestion);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Cover Photo Generator</CardTitle>
        <CardDescription className="text-xs">
          Search and generate professional photos for your proposal cover
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Input
            placeholder="Search for cover photos..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={loading}
            className="flex-1"
          />
          <Button 
            onClick={handleSearch} 
            disabled={loading || !searchQuery.trim()}
            size="default"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Search className="w-4 h-4" />
            )}
          </Button>
        </div>

        <div className="flex flex-wrap gap-2">
          <span className="text-xs text-muted-foreground">Quick:</span>
          {quickSuggestions.map((suggestion) => (
            <Button
              key={suggestion}
              variant="outline"
              size="sm"
              onClick={() => handleQuickSearch(suggestion)}
              disabled={loading}
              className="text-xs h-7"
            >
              {suggestion}
            </Button>
          ))}
        </div>

        {loading && (
          <div className="flex flex-col items-center justify-center py-8 space-y-2">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Generating beautiful photos...</p>
          </div>
        )}

        {!loading && photos.length > 0 && (
          <>
            <div className="grid grid-cols-2 gap-2">
              {photos.map((photo) => (
                <button
                  key={photo.name}
                  onClick={() => onSelectPhoto(photo.url)}
                  className="relative overflow-hidden rounded-lg border-2 border-transparent hover:border-primary transition-all group"
                >
                  <img 
                    src={photo.url} 
                    alt={photo.name}
                    className="w-full h-32 object-cover group-hover:scale-105 transition-transform"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <span className="text-white text-xs font-medium">Select</span>
                  </div>
                </button>
              ))}
            </div>
            <Button 
              onClick={() => generatePhotos(searchQuery)} 
              variant="outline"
              className="w-full"
              size="sm"
              disabled={loading}
            >
              Generate More Variations
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
